import {
  Datagrid,
  TextField,
  ReferenceField,
  useListContext,
  FunctionField,
  useNotify,
  useRefresh,
  SimpleList,
} from "react-admin";
import { Button, useMediaQuery } from "@mui/material";
import {
  MOBILE_BREAKPOINT,
  StudentServicePath,
  WEEKLY_SCHEDULE_DISCIPLINES,
  WeeklyScheduleStatuses,
  WeeklyScheduleStatusesTexts,
} from "utils/constants";
import UserField from "server/components/Fields/UserField";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import WeeklyScheduleExpanded from "./WeeklyScheduleExpanded";
import WeeklyScheduleMobileExpanded from "./WeeklyScheduleMobileExpanded";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

import NotifyDepartmentsButton from "./NotifyDepartmentsButton";
import { studentRequests } from "api/studentApi";
import { administrationRequests } from "api/administrationApi";

const WeeklyScheduleTable = () => {
  const { data, isLoading } = useListContext();
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  if (isLoading || !data || data.length === 0) return null;

  const handleApproveSchedule = async id => {
    await dataProvider.customRequest(
      studentRequests.updateWeeklyScheduleStatus(id),
      { status: WeeklyScheduleStatuses.Approved },
      "PUT",
    );
    notify("Одобрихте успешно", { type: "success" });
    refresh();
  };

  const renderActionButtons = record => (
    <>
      <SecuredComponent permission={permissions.canPreviewWeeklySchedule}>
        <Button
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(`/${StudentServicePath}${WEEKLY_SCHEDULE_DISCIPLINES}?weeklyScheduleId=${record.id}`)}
        >
          Прегледай
        </Button>
      </SecuredComponent>
      {record.status === WeeklyScheduleStatuses.New && (
        <SecuredComponent permission={permissions.canUpdateWeeklyScheduleStatus}>
          <ConfirmModal
            titleContent="Сигурни ли сте, че искате да одобрите седмичното разписание?"
            onConfirm={() => handleApproveSchedule(record.id)}
            triggerButton={
              <Button
                startIcon={<CheckCircleIcon />}
                size="small"
                sx={{ margin: 0.5 }}
              >
                Одобри
              </Button>
            }
          />
        </SecuredComponent>
      )}
      <SecuredComponent permission={permissions.canNotifyDepartmentsForWeeklyScheduleCreation}>
        <NotifyDepartmentsButton />
      </SecuredComponent>
    </>
  );

  if (isMobile) {
    return (
      <SimpleList
        linkType={false}
        sx={settings.style.mobileList}
        primaryText={record => (
          <div>
            <MobileListRow fields={[{ label: "Дисциплини", value: record?.disciplineId }]} />
            <MobileListRow fields={[{ label: "ID", value: record?.id }]} />
          </div>
        )}
        secondaryText={record => (
          <div>
            <MobileListRow
              fields={[
                {
                  label: "Статус",
                  value: WeeklyScheduleStatusesTexts[record.status] || "Неизвестен",
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Специалност",
                  value: (
                    <ReferenceField
                      source="specialityId"
                      reference={administrationRequests.getSpecialitiesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow fields={[{ label: "Учебен план", value: record?.curriculumName }]} />
            <MobileListRow
              fields={[
                {
                  label: "Език на обучение",
                  value: (
                    <ReferenceField
                      source="instructionLanguageId"
                      reference={administrationRequests.getInstructionLanguagesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Вид на обучение",
                  value: (
                    <ReferenceField
                      source="studyTypeId"
                      reference={administrationRequests.getStudyTypesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Семестър",
                  value: (
                    <ReferenceField
                      source="academicYearSessionId"
                      reference={studentRequests.getAcademicSessionsSelect()}
                    >
                      <TextField source="sessionTypeText" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Създадено от",
                  value: <UserField source="createdByUserId" />,
                },
              ]}
            />
            <MobileListRow fields={[{ label: "Създадено на", value: record?.createdOnFormatted }]} />
            {renderActionButtons(record)}
            <WeeklyScheduleMobileExpanded record={record} />
          </div>
        )}
      />
    );
  }

  return (
    <Datagrid
      // expandSingle={({ record }) => <WeeklyScheduleExpanded weeklyScheduleRecord={record} />}
      expand={<WeeklyScheduleExpanded />}
      expandSingle
      rowClick={false}
      bulkActionButtons={false}
    >
      <TextField
        source="disciplineId"
        label="Дисциплини"
        sortable={false}
      />
      <TextField
        source="id"
        label="ID"
        sortable={false}
      />

      <FunctionField
        label="Статус"
        render={record => WeeklyScheduleStatusesTexts[record.status] || "Неизвестен"}
        sortable={false}
      />

      <ReferenceField
        source="specialityId"
        reference={administrationRequests.getSpecialitiesSelect()}
        label="Специалност"
        sortable={false}
      >
        <TextField source="name" />
      </ReferenceField>

      <TextField
        sortable={false}
        source="curriculumName"
        label="Учебен план"
      />
      <ReferenceField
        sortable={false}
        source="instructionLanguageId"
        reference={administrationRequests.getInstructionLanguagesSelect()}
        label="Език на обучение"
      >
        <TextField source="name" />
      </ReferenceField>

      <ReferenceField
        sortable={false}
        source="studyTypeId"
        reference={administrationRequests.getStudyTypesSelect()}
        label="Вид на обучение"
      >
        <TextField source="name" />
      </ReferenceField>

      <ReferenceField
        sortable={false}
        source="academicYearSessionId"
        reference={studentRequests.getAcademicSessionsSelect()}
        label="Семестър"
      >
        <TextField source="sessionTypeText" />
      </ReferenceField>

      <UserField
        sortable={false}
        label="Създадено от"
        source="createdByUserId"
      />

      <TextField
        sortable={false}
        label="Създадено на"
        source="createdOnFormatted"
      />

      <FunctionField render={record => renderActionButtons(record)} />
    </Datagrid>
  );
};

export default WeeklyScheduleTable;
